import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Handles the Emergent Google Auth redirect: reads session_id from URL fragment,
 * exchanges it for an ABM JWT via /api/auth/google/session, then navigates home.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function GoogleAuthCallback() {
  const location = useLocation();
  const nav = useNavigate();
  const { loginWithGoogleSession } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const raw = location.hash || "";
    const match = raw.match(/session_id=([^&]+)/);
    if (!match) {
      toast.error("Could not find your Google session");
      nav("/login", { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      const res = await loginWithGoogleSession(sessionId);
      // Strip the fragment so refresh doesn't retry.
      window.history.replaceState({}, "", window.location.pathname + window.location.search);
      if (res.ok) {
        toast.success("Signed in with Google");
        nav("/", { replace: true });
      } else {
        toast.error(res.error || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [location.hash, loginWithGoogleSession, nav]);

  return (
    <div className="min-h-[70vh] grid place-items-center text-center px-6" data-testid="google-callback">
      <div>
        <Loader2 className="w-8 h-8 animate-spin text-[#C05A3D] mx-auto" />
        <div className="mt-4 font-display text-xl text-[#1E3A2F]">Finishing your sign-in…</div>
        <p className="text-sm text-[#5C5C5C] mt-1">Bringing you into the community.</p>
      </div>
    </div>
  );
}
